import { themeSelector } from '@reduxLocal/selectors/themeSelector';

// App's primary color palette
export const APP_COLORS = {
	primary: {
		blue: '#3b82f6',
		indigo: '#6366f1',
		purple: '#8b5cf6',
		violet: '#7c3aed',
	},
	success: {
		green: '#10b981',
		emerald: '#059669',
		teal: '#14b8a6',
	},
	warning: {
		amber: '#f59e0b',
		orange: '#f97316',
		yellow: '#eab308',
	},
	error: {
		red: '#ef4444',
		rose: '#f43f5e',
		pink: '#ec4899',
	},
	neutral: {
		grey: '#6b7280',
		slate: '#64748b',
		zinc: '#71717a',
		stone: '#78716c',
	},
	gradients: {
		bluePurple: ['#3b82f6', '#8b5cf6'],
		greenTeal: ['#10b981', '#14b8a6'],
		amberOrange: ['#f59e0b', '#f97316'],
		purpleViolet: ['#8b5cf6', '#7c3aed'],
	},
};

// Chart color schemes
export const CHART_COLORS = {
	primary: [
		APP_COLORS.primary.blue,
		APP_COLORS.success.green,
		APP_COLORS.warning.amber,
		APP_COLORS.primary.purple,
	],
	modern: [
		APP_COLORS.primary.indigo,
		APP_COLORS.success.emerald,
		APP_COLORS.warning.orange,
		APP_COLORS.primary.violet,
	],
	vibrant: [
		APP_COLORS.primary.blue,
		APP_COLORS.success.teal,
		APP_COLORS.warning.yellow,
		APP_COLORS.error.pink,
	],
	professional: [
		APP_COLORS.primary.blue,
		APP_COLORS.success.green,
		APP_COLORS.neutral.slate,
		APP_COLORS.warning.amber,
	],
};

// Theme-based chart configuration
export const getChartConfig = (
	colorScheme: keyof typeof CHART_COLORS = 'modern',
	theme?: boolean
) => {
	const isLightTheme = theme !== undefined ? theme : themeSelector();
	const colors = CHART_COLORS[colorScheme];

	return {
		backgroundColor: isLightTheme ? '#ffffff' : '#1e1e1e',
		backgroundGradientFrom: isLightTheme ? '#ffffff' : '#1e1e1e',
		backgroundGradientTo: isLightTheme ? '#f8fafc' : '#2d2d2d',
		backgroundGradientFromOpacity: 1,
		backgroundGradientToOpacity: 1,
		color: (opacity = 1) =>
			`${colors[0]}${Math.round(opacity * 255)
				.toString(16)
				.padStart(2, '0')}`,
		strokeWidth: 3,
		barPercentage: 0.8,
		useShadowColorFromDataset: false,
		decimalPlaces: 0,
		labelColor: (opacity = 1) =>
			isLightTheme
				? `rgba(0, 0, 0, ${opacity})`
				: `rgba(255, 255, 255, ${opacity})`,
		propsForLabels: {
			fontSize: 12,
			fontWeight: '600',
			fill: isLightTheme ? '#374151' : '#d1d5db',
		},
		propsForDots: {
			r: '6',
			strokeWidth: '3',
			stroke: colors[0],
		},
		propsForBackgroundLines: {
			strokeDasharray: '',
			stroke: isLightTheme
				? 'rgba(0, 0, 0, 0.1)'
				: 'rgba(255, 255, 255, 0.1)',
			strokeWidth: 1,
		},
		fillShadowGradient: colors[0],
		fillShadowGradientOpacity: 0.3,
		style: {
			borderRadius: 16,
			padding: 16,
		},
		formatYLabel: (label: string) => {
			const num = parseInt(label);
			if (num >= 1000) return `${(num / 1000).toFixed(1)}k`;
			return num.toString();
		},
	};
};

// Legend styling configuration
export const getLegendConfig = (theme: boolean) => ({
	legendFontSize: 14,
	legendFontColor: theme ? '#374151' : '#d1d5db',
	legendFontFamily: 'System',
	legendFontWeight: '600' as const,
	legendTextColor: theme ? '#374151' : '#d1d5db',
	legendPosition: 'bottom' as const,
	legendAlign: 'center' as const,
	legendOffset: 20,
	legendOffsetX: 0,
	legendOffsetY: 0,
});

// Pie chart specific configuration
export const getPieChartConfig = (
	colorScheme: keyof typeof CHART_COLORS = 'modern',
	theme?: boolean
) => {
	const isLightTheme = theme !== undefined ? theme : themeSelector();
	const colors = CHART_COLORS[colorScheme];
	const legendConfig = getLegendConfig(isLightTheme);

	return {
		...getChartConfig(colorScheme, isLightTheme),
		...legendConfig,
		colors: colors,
		style: {
			borderRadius: 16,
			padding: 16,
		},
		accessor: 'population',
		backgroundColor: 'transparent',
		paddingLeft: '20',
		hasLegend: true,
		center: [10, 10],
		absolute: false,
	};
};

// Bar chart specific configuration
export const getBarChartConfig = (
	colorScheme: keyof typeof CHART_COLORS = 'modern',
	theme?: boolean
) => {
	const isLightTheme = theme !== undefined ? theme : themeSelector();
	const colors = CHART_COLORS[colorScheme];

	return {
		...getChartConfig(colorScheme, isLightTheme),
		colors: colors,
		style: {
			borderRadius: 16,
			padding: 16,
		},
		showValuesOnTopOfBars: true,
		showBarTops: false,
		yAxisLabel: '',
		yAxisSuffix: '',
		xAxisLabel: '',
		fromZero: true,
		horizontalLabelRotation: 0,
		verticalLabelRotation: 0,
	};
};

// Line chart specific configuration
export const getLineChartConfig = (
	colorScheme: keyof typeof CHART_COLORS = 'modern',
	theme?: boolean
) => {
	const isLightTheme = theme !== undefined ? theme : themeSelector();
	const colors = CHART_COLORS[colorScheme];

	return {
		...getChartConfig(colorScheme, isLightTheme),
		colors: colors,
		style: {
			borderRadius: 16,
			padding: 16,
		},
		bezier: true,
		bezierWight: 0.4,
		withHorizontalLabels: true,
		withVerticalLabels: true,
		withDots: true,
		withShadow: true,
		withScrollableDot: false,
		withInnerLines: true,
		withOuterLines: true,
	};
};

// Generate consistent data colors
export const getDataColors = (
	colorScheme: keyof typeof CHART_COLORS = 'modern',
	count: number
) => {
	const colors = CHART_COLORS[colorScheme];
	const result = [];

	for (let i = 0; i < count; i++) {
		result.push(colors[i % colors.length]);
	}

	return result;
};

// Modern color palette for different chart types
export const MODERN_CHART_COLORS = {
	line: {
		primary: APP_COLORS.primary.blue,
		secondary: APP_COLORS.success.green,
		tertiary: APP_COLORS.warning.amber,
	},
	bar: {
		primary: APP_COLORS.primary.indigo,
		secondary: APP_COLORS.success.emerald,
		tertiary: APP_COLORS.warning.orange,
	},
	pie: {
		primary: APP_COLORS.primary.purple,
		secondary: APP_COLORS.success.teal,
		tertiary: APP_COLORS.warning.yellow,
		quaternary: APP_COLORS.error.pink,
	},
};

export default {
	APP_COLORS,
	CHART_COLORS,
	getChartConfig,
	getLegendConfig,
	getPieChartConfig,
	getBarChartConfig,
	getLineChartConfig,
	getDataColors,
	MODERN_CHART_COLORS,
};
